// Every text of the interface, in English and German. A value is either a
// string with {placeholders} or a function of the placeholders, for texts
// whose wording depends on a number (plurals) or on another text.
// The German texts address the user with "du", like the AI prompts do.

export const LANGUAGES = ["en", "de"];
export const DEFAULT_LANGUAGE = "en";

const en = {
  common: {
    untitled: "Untitled",
    cancel: "Cancel",
    save: "Save",
    close: "Close",
    dismiss: "Dismiss",
    completed: "Completed",
    done: "✓ Done",
    noCardsYet: "No cards yet",
    timeLeft: "Time left",
    start: (p) => `Start ${p.time}`,
    total: (p) => `Total ${p.duration}`,
    logo: "focussuite.de logo",
  },
  footer: {
    board: "Configure Agenda",
    ai: "Plan with AI",
    imprint: "Imprint",
    privacy: "Privacy",
    examples: "Examples",
    switchLanguage: "Deutsch",
    switchLanguageLabel: "Auf Deutsch umschalten",
  },
  board: {
    noCards: "No cards on the table yet",
    cards: (p) => `${p.count} card${p.count === 1 ? "" : "s"} on the table`,
    total: (p) => `${p.duration} total`,
    addCard: "Add a card",
    onTheTable: "On the table",
    hidePast: "Hide past cards",
    setTimer: "Set timer",
    soundAlerts: "Sound alerts",
    warnBefore: "Warn before card ends",
    allDone: "All cards are marked done.",
  },
  addCard: {
    name: "Name (optional)",
    maxCharacters: (p) => `Max. ${p.count} characters`,
    timebox: "Timebox (hh:mm:ss)",
    add: "+ Add",
  },
  timer: {
    starttime: "Select Starttime",
    resetAll: "Reset All",
    restart: "Restart",
    openFullPage: "Open full-page timer view (no browser fullscreen)",
    enterFullscreen: "Show timer fullscreen",
    exitFullscreen: "Exit fullscreen",
    mute: "Mute sound alerts",
    unmute: "Unmute sound alerts",
  },
  warningLead: {
    seconds: (p) => `${p.count} seconds`,
    minutes: (p) => `${p.count} minute${p.count === 1 ? "" : "s"}`,
  },
  mini: {
    open: "Open the mini timer: a small window that stays on top, also while sharing your screen",
    close: "Close the mini timer",
    next: "Next:",
    first: "First:",
    startsAt: (p) => `Starts at ${p.time}`,
  },
  live: {
    pause: "Pause",
    resume: "Resume",
    plusOne: "Give the current card 1 more minute",
    plusFive: "Give the current card 5 more minutes",
    next: "Next",
    nextLabel: "End the current card now and start the next one",
    paused: "Paused",
    behind: (p) => `${p.minutes} min behind plan`,
    ahead: (p) => `${p.minutes} min ahead of plan`,
    onPlan: "On plan",
  },
  card: {
    tagDone: "✓ Done",
    tagNow: "● Now",
    tagNext: "→ Next",
    drag: "Drag to reorder card",
    markCompleted: (p) => `Mark "${p.topic}" as completed`,
    markDone: "Mark as completed",
    markNotDone: "Mark as not completed",
    moreActions: "More actions",
    name: "Card name",
    editDetails: "Edit details",
    complete: "Complete",
    completeActive: "Ends now, next card starts right away",
    completeOther: "Tick off as completed",
    details: "Details",
    detailsHint: "Type, who runs it, note",
    duplicate: "Duplicate",
    duplicateHint: "Add a copy right after",
    delete: "Delete",
    deleteHint: "Remove this card",
    placeholderName: (p) => `Card ${p.number}`,
  },
  cardDetails: {
    type: "Type",
    none: "None",
    owner: "Who runs it",
    ownerPlaceholder: "e.g. Anna",
    note: "Note",
    notePlaceholder: "Goal, method, material, ...",
  },
  cardTypes: {
    break: "Break",
    input: "Input",
    work: "Group work",
    discussion: "Discussion",
    workout: "Workout",
  },
  share: {
    button: "Share",
    title: "Share this plan",
    linkOpens: "Link opens",
    viewBoard: "Board",
    viewFullscreen: "Full-screen timer",
    hintFullscreen:
      "Participants scan the code and follow along on their phone.",
    hintBoard: "Opens the editable board, e.g. for a co-facilitator.",
    qrTitle: "QR code for this plan",
    link: "Link",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    shareNative: "Share…",
    copyText: "Copy as text",
    textCopied: "Plan copied as text",
    calendar: "Add to calendar",
    fileName: "agenda",
  },
  exports: {
    agenda: "Agenda",
    liveTimer: "Live timer",
    owner: (p) => `owner: ${p.name}`,
    note: (p) => `Note: ${p.note}`,
    minutes: (p) => `${p.minutes} min`,
  },
  duration: {
    seconds: (p) => `${p.seconds} s`,
    minutesSeconds: (p) => `${p.minutes} min ${p.seconds} s`,
    minutes: (p) => `${p.minutes} min`,
    hours: (p) => `${p.hours}h`,
    hoursMinutes: (p) => `${p.hours}h ${p.minutes}min`,
  },
  editWithAi: {
    button: "Edit with AI",
    openIn: (p) => `Open in ${p.name}`,
    copyFirstHint: "Prompt is copied too, paste it if the chat is empty",
    prefilledHint: "Your plan is filled in, just add the change",
    copyPrompt: "Copy prompt",
    copyPromptHint: "For any other assistant",
    copied: "Prompt copied - paste it into your assistant",
  },
  aiPlanner: {
    title: "Plan with AI",
    subtitle:
      "Let ChatGPT, Claude or Copilot plan your session, then run it here as a timer",
    howItWorks: "How it works",
    step1: "Open your AI assistant with the prompt below.",
    step2:
      "Tell it about your meeting, workshop or study block. It asks for anything it still needs and proposes an agenda.",
    step3: "Click the link it gives you. Your plan opens here, ready to run.",
    promptLanguage: "Language of the prompt",
    openIn: (p) => `Open in ${p.name}`,
    copyPrompt: "Copy prompt",
    otherTitle: "Using another assistant?",
    otherBefore:
      "Copy the prompt into any chat (Gemini, Le Chat, ...). The prompt explains the link format, and assistants that can browse find the full guide at",
    changeTitle: "Changing a plan later",
    changeBefore: "On the board,",
    changeButton: "Edit with AI",
    changeAfter:
      'hands your current plan back to the assistant, so you can say things like "make it 30 minutes shorter" or "add a lunch break at 12:00".',
    copied: "Prompt copied",
  },
  examples: {
    title: "Examples",
    subtitle:
      "Pick a template to open a ready-made agenda - it starts right away, and you can adjust every card",
    categories: "Template categories",
    groups: "Template groups",
    work: "Work",
    sport: "Sport",
    cooking: "Cooking",
    everyday: "Everyday",
    meetings: "Meetings",
    workshops: "Workshops",
    retros: "Retrospectives",
    scrum: "Scrum",
    talks: "Talks & Learning",
    sportFitness: "Sport & Fitness",
    eggs: "Eggs",
    kitchen: "Kitchen Basics",
    dough: "Yeast Dough",
    everydayLife: "Everyday Life",
  },
  legal: {
    germanOnly:
      "This page is only available in German, the language it is legally required in.",
  },
  linkNotice: {
    title: "Parts of this link couldn't be read:",
    ignored: (p) => `Ignored "${p.part}" on "${p.topic}".`,
    noDuration: (p) => `"${p.topic}" had no duration, so it got 10 minutes.`,
    badDuration: (p) =>
      `Couldn't read the duration "${p.duration}" of "${p.topic}", so it got 10 minutes.`,
    unknownType: (p) => `Unknown card type "${p.type}" on "${p.topic}".`,
    badStart: (p) =>
      `Couldn't read the start time "${p.start}", so the plan starts now.`,
    badCards: "The cards in the link couldn't be read, so they were left out.",
  },
};

const de = {
  common: {
    untitled: "Ohne Titel",
    cancel: "Abbrechen",
    save: "Speichern",
    close: "Schließen",
    dismiss: "Ausblenden",
    completed: "Erledigt",
    done: "✓ Fertig",
    noCardsYet: "Noch keine Karten",
    timeLeft: "Restzeit",
    start: (p) => `Start ${p.time}`,
    total: (p) => `Gesamt ${p.duration}`,
    logo: "focussuite.de-Logo",
  },
  footer: {
    board: "Agenda planen",
    ai: "Mit KI planen",
    imprint: "Impressum",
    privacy: "Datenschutz",
    examples: "Vorlagen",
    switchLanguage: "English",
    switchLanguageLabel: "Switch to English",
  },
  board: {
    noCards: "Noch keine Karten auf dem Tisch",
    cards: (p) =>
      `${p.count} ${p.count === 1 ? "Karte" : "Karten"} auf dem Tisch`,
    total: (p) => `${p.duration} gesamt`,
    addCard: "Karte hinzufügen",
    onTheTable: "Auf dem Tisch",
    hidePast: "Vergangene ausblenden",
    setTimer: "Timer stellen",
    soundAlerts: "Signaltöne",
    warnBefore: "Warnen vor Kartenende",
    allDone: "Alle Karten sind erledigt.",
  },
  addCard: {
    name: "Name (optional)",
    maxCharacters: (p) => `Max. ${p.count} Zeichen`,
    timebox: "Timebox (hh:mm:ss)",
    add: "+ Hinzufügen",
  },
  timer: {
    starttime: "Startzeit wählen",
    resetAll: "Alles zurücksetzen",
    restart: "Neu starten",
    openFullPage: "Timer als ganze Seite öffnen (ohne Browser-Vollbild)",
    enterFullscreen: "Timer im Vollbild zeigen",
    exitFullscreen: "Vollbild beenden",
    mute: "Signaltöne ausschalten",
    unmute: "Signaltöne einschalten",
  },
  warningLead: {
    seconds: (p) => `${p.count} Sekunden`,
    minutes: (p) => `${p.count} ${p.count === 1 ? "Minute" : "Minuten"}`,
  },
  mini: {
    open: "Mini-Timer öffnen: kleines Fenster, das immer im Vordergrund bleibt, auch beim Bildschirmteilen",
    close: "Mini-Timer schließen",
    next: "Danach:",
    first: "Zuerst:",
    startsAt: (p) => `Beginnt um ${p.time}`,
  },
  live: {
    pause: "Pause",
    resume: "Weiter",
    plusOne: "Der laufenden Karte 1 Minute mehr geben",
    plusFive: "Der laufenden Karte 5 Minuten mehr geben",
    next: "Nächste",
    nextLabel: "Laufende Karte jetzt beenden und die nächste starten",
    paused: "Pausiert",
    behind: (p) => `${p.minutes} Min. hinter dem Plan`,
    ahead: (p) => `${p.minutes} Min. vor dem Plan`,
    onPlan: "Im Plan",
  },
  card: {
    tagDone: "✓ Vorbei",
    tagNow: "● Jetzt",
    tagNext: "→ Gleich",
    drag: "Ziehen, um die Karte zu verschieben",
    markCompleted: (p) => `„${p.topic}“ als erledigt markieren`,
    markDone: "Als erledigt markieren",
    markNotDone: "Als nicht erledigt markieren",
    moreActions: "Weitere Aktionen",
    name: "Kartenname",
    editDetails: "Details bearbeiten",
    complete: "Erledigt",
    completeActive: "Endet jetzt, die nächste Karte startet sofort",
    completeOther: "Als erledigt abhaken",
    details: "Details",
    detailsHint: "Typ, wer sie leitet, Notiz",
    duplicate: "Duplizieren",
    duplicateHint: "Direkt dahinter eine Kopie einfügen",
    delete: "Löschen",
    deleteHint: "Diese Karte entfernen",
    placeholderName: (p) => `Karte ${p.number}`,
  },
  cardDetails: {
    type: "Typ",
    none: "Keiner",
    owner: "Wer leitet sie",
    ownerPlaceholder: "z. B. Anna",
    note: "Notiz",
    notePlaceholder: "Ziel, Methode, Material, ...",
  },
  cardTypes: {
    break: "Pause",
    input: "Input",
    work: "Gruppenarbeit",
    discussion: "Diskussion",
    workout: "Sportübung",
  },
  share: {
    button: "Teilen",
    title: "Plan teilen",
    linkOpens: "Link öffnet",
    viewBoard: "Board",
    viewFullscreen: "Vollbild-Timer",
    hintFullscreen:
      "Teilnehmende scannen den Code und verfolgen den Ablauf auf dem Handy.",
    hintBoard: "Öffnet das bearbeitbare Board, z. B. für die Co-Moderation.",
    qrTitle: "QR-Code für diesen Plan",
    link: "Link",
    copyLink: "Link kopieren",
    linkCopied: "Link kopiert",
    shareNative: "Teilen…",
    copyText: "Als Text kopieren",
    textCopied: "Plan als Text kopiert",
    calendar: "Zum Kalender hinzufügen",
    fileName: "agenda",
  },
  exports: {
    agenda: "Agenda",
    liveTimer: "Live-Timer",
    owner: (p) => `verantwortlich: ${p.name}`,
    note: (p) => `Notiz: ${p.note}`,
    minutes: (p) => `${p.minutes} Min.`,
  },
  duration: {
    seconds: (p) => `${p.seconds} Sek.`,
    minutesSeconds: (p) => `${p.minutes} Min. ${p.seconds} Sek.`,
    minutes: (p) => `${p.minutes} Min.`,
    hours: (p) => `${p.hours} Std.`,
    hoursMinutes: (p) => `${p.hours} Std. ${p.minutes} Min.`,
  },
  editWithAi: {
    button: "Mit KI ändern",
    openIn: (p) => `In ${p.name} öffnen`,
    copyFirstHint:
      "Der Prompt wird auch kopiert, füg ihn ein, falls der Chat leer ist",
    prefilledHint: "Dein Plan ist schon drin, schreib nur die Änderung dazu",
    copyPrompt: "Prompt kopieren",
    copyPromptHint: "Für jeden anderen Assistenten",
    copied: "Prompt kopiert – füg ihn in deinen Assistenten ein",
  },
  aiPlanner: {
    title: "Mit KI planen",
    subtitle:
      "Lass ChatGPT, Claude oder Copilot deinen Termin planen und führ ihn hier als Timer durch",
    howItWorks: "So funktioniert’s",
    step1: "Öffne deinen KI-Assistenten mit dem Prompt unten.",
    step2:
      "Erzähl ihm von deinem Meeting, Workshop oder deiner Lerneinheit. Er fragt nach, was noch fehlt, und schlägt einen Ablauf vor.",
    step3:
      "Klick auf den Link, den er dir gibt. Dein Plan öffnet sich hier und kann sofort losgehen.",
    promptLanguage: "Sprache des Prompts",
    openIn: (p) => `In ${p.name} öffnen`,
    copyPrompt: "Prompt kopieren",
    otherTitle: "Du nutzt einen anderen Assistenten?",
    otherBefore:
      "Kopier den Prompt in einen beliebigen Chat (Gemini, Le Chat, ...). Der Prompt erklärt das Link-Format, und Assistenten mit Internetzugriff finden die vollständige Anleitung unter",
    changeTitle: "Einen Plan später ändern",
    changeBefore: "Auf dem Board gibt",
    changeButton: "Mit KI ändern",
    changeAfter:
      "deinen aktuellen Plan an den Assistenten zurück. Dann kannst du einfach sagen: „Mach es 30 Minuten kürzer“ oder „Füg um 12:00 eine Mittagspause ein“.",
    copied: "Prompt kopiert",
  },
  examples: {
    title: "Vorlagen",
    subtitle:
      "Wähl eine Vorlage für eine fertige Agenda – sie startet sofort, und du kannst jede Karte anpassen",
    categories: "Vorlagen-Kategorien",
    groups: "Vorlagen-Gruppen",
    work: "Arbeit",
    sport: "Sport",
    cooking: "Kochen",
    everyday: "Alltag",
    meetings: "Meetings",
    workshops: "Workshops",
    retros: "Retrospektiven",
    scrum: "Scrum",
    talks: "Vorträge & Lernen",
    sportFitness: "Sport & Fitness",
    eggs: "Eier",
    kitchen: "Küchen-Basics",
    dough: "Hefeteig",
    everydayLife: "Alltag",
  },
  legal: {
    germanOnly: null,
  },
  linkNotice: {
    title: "Teile dieses Links konnten nicht gelesen werden:",
    ignored: (p) => `„${p.part}“ bei „${p.topic}“ ignoriert.`,
    noDuration: (p) => `„${p.topic}“ hatte keine Dauer und bekommt 10 Minuten.`,
    badDuration: (p) =>
      `Die Dauer „${p.duration}“ von „${p.topic}“ war nicht lesbar, die Karte bekommt 10 Minuten.`,
    unknownType: (p) => `Unbekannter Kartentyp „${p.type}“ bei „${p.topic}“.`,
    badStart: (p) =>
      `Die Startzeit „${p.start}“ war nicht lesbar, der Plan startet jetzt.`,
    badCards:
      "Die Karten im Link waren nicht lesbar und wurden weggelassen.",
  },
};

export const messages = { en, de };

function lookup(language, key) {
  return key
    .split(".")
    .reduce((node, part) => (node == null ? node : node[part]), messages[language]);
}

// The text for a dotted key ("board.addCard") in the given language,
// falling back to English for a missing key.
export function translate(language, key, params = {}) {
  let value = lookup(language, key);
  if (value === undefined) {
    value = lookup(DEFAULT_LANGUAGE, key);
  }
  if (value === undefined) {
    return key;
  }
  return typeof value === "function" ? value(params) : value;
}
